import React, { useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import classNames from 'classnames';
import Icon, { COLORS, ICONS } from './general-components/icon';
import { carouselSkele } from './style-packs/skeleton-sizes';
import StylePackItem from './style-packs/style-pack-item';
import { ContentTemplate, StylePackItemTypes } from './types/stylepacks';

// unfortunately the items are grouped by type since they are translated
// from a record with the items in an array, so all the walkups are
// together, all the lowers, etc - this is restoring the order
const sortOrderIndex = (a:ContentTemplate,b:ContentTemplate) => {
	if (a.order_index && b.order_index) {
		return a.order_index - b.order_index;
	}
	return 0;
}

enum SCROLL {
	RIGHT = 'right',
	LEFT = 'left',
	NONE = 'none',
}

export interface CarouselProps {
    id: string;
	renderItem?: (item: any, index: number) => JSX.Element;
    list: any[];
    onClick?: (item: any, index: number) => void;
    itemWidth?: number;
	drawerOpening?: boolean;
	visibleItems?: number;
}

export default function Carousel(props: CarouselProps) {
	const {
		id,
		list = [],
		onClick,
		itemWidth = 193, // item width (185px) + margin right (8px)
		drawerOpening = false,
		visibleItems = 2,
	} = props;

	const [arrows, setArrows] = useState({ left: false, right: list.length >= visibleItems + 1});
	const timeout = useRef<NodeJS.Timeout>();
	const trackRef = useRef<HTMLDivElement>(null);

	function scrollCarousel(direction: SCROLL) {
		if (trackRef.current) {
			const nArrows = {left: true, right: true};
			if (trackRef.current.scrollLeft - itemWidth < 0) {
				nArrows.left = false;
			}
			if (trackRef.current.scrollLeft + itemWidth + trackRef.current.clientWidth > trackRef.current.scrollWidth) {
				nArrows.right = false;
			} else if (list.length < visibleItems + 1) {
				nArrows.right = false;
			}
			setArrows(nArrows);
			
			trackRef.current.scrollBy({
				left: direction === SCROLL.LEFT ? itemWidth * -1 : itemWidth,
				behavior: 'smooth'
			});
		}
	}
	
	return (
		<div className='carousel'>
			<div
				className="carousel-track"
				data-testid="carousel"
				id={id}
				ref={trackRef}
				onScroll={() => {
					if (timeout.current) clearTimeout(timeout.current);
					timeout.current = setTimeout(() => {
						if (trackRef.current) {
							const nArrows = {left: true, right: true};
							if (trackRef.current.scrollLeft === 0) {
								nArrows.left = false;
							}
							if (trackRef.current.scrollLeft + trackRef.current.clientWidth >= trackRef.current.scrollWidth) {
								nArrows.right = false;
							}
							setArrows(nArrows);
						}
					}, 30);
				}}
			>
				{list.sort(sortOrderIndex).map((item, index) => {
					if(!item) {
						// The carousel adds an empty item for title stylepacks
						return null;
					}
					return(
					<div
						className="carousel-item"
						data-testid={`carousel-item-${index}`}
						key={item.id || index}
						onClick={() => onClick?.(item, index)}
					>
						{drawerOpening
							? <Skeleton width={185} height={105} style={{
								marginRight: '8px',
								borderRadius: '8px',
								top:'-3px'
							}}/>
							: <StylePackItem
								key={`style-pack-carousel-item-${item.id}`}
								item={item}
								type={StylePackItemTypes.Carousel}
								skeletonSize={{...carouselSkele, style:{top:'-2px'}}}
							/>
						}
					</div>
				)})}
			</div>
			<div
				className={classNames('carousel-gradient right', {
					visible: drawerOpening || (arrows.right && list.length > visibleItems),
				})}
				data-testid="carousel-right-gradient"
			/>
			<div
				className={classNames("carousel-arrow left", {visible: arrows.left && list.length > visibleItems})}
				data-testid="carousel-left-arrow"
				onClick={(e) => {
					e.stopPropagation();
					e.preventDefault();
					scrollCarousel(SCROLL.LEFT);
				}}
			>
				<Icon
					name={ICONS.ARROW_BUTTON_LEFT}
					color={COLORS.WHITE}
					size={20}
				/>
			</div>
			<div
				className={classNames('carousel-arrow right', {visible: arrows.right})}
				data-testid="carousel-right-arrow"
				onClick={(e) => {
					e.stopPropagation();
					e.preventDefault();
					scrollCarousel(SCROLL.RIGHT);
				}}
			>
				<Icon
					name={ICONS.ARROW_BUTTON_RIGHT}
					color={COLORS.WHITE}
					size={20}
				/>
			</div>
		</div>
	);
}
